'use client';

import React from "react";
import Link from "next/link";
import { search, SearchHit } from "@/lib/search";

interface SearchResult extends SearchHit {
  objectID: string;
  title: string;
  excerpt: string;
  uri: string;
}

interface SearchEvent extends CustomEvent {
  detail: string;
}

export default function SearchResults() {
  const [query, setQuery] = React.useState<string>("");
  const [results, setResults] = React.useState<SearchResult[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const performSearch = React.useCallback(async (searchQuery: string) => {
    if (!searchQuery) return;
    setQuery(searchQuery);
    setLoading(true);
    setError(null);
    try {
      const result = await search(searchQuery);
      setResults(result.hits);
    } catch (err) {
      setError(err instanceof Error ? err.message : "検索中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    // Handle search event
    const handleSearch = (event: SearchEvent) => {
      performSearch(event.detail);
    };

    // Handle browser navigation
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const searchQuery = params.get("q");
      if (searchQuery) {
        performSearch(searchQuery);
      } else {
        setQuery("");
        setResults([]);
      }
    };

    // Initial search
    const params = new URLSearchParams(window.location.search);
    const searchQuery = params.get("q");
    if (searchQuery) {
      performSearch(searchQuery);
    }

    // Add event listeners
    window.addEventListener('search', handleSearch as EventListener);
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('search', handleSearch as EventListener);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [performSearch]);

  if (!query && !loading) {
    return null;
  }

  if (loading) {
    return <div className="text-center py-8">検索中...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-8">{error}</div>;
  }

  if ((!results || results.length === 0) && query) {
    return (
      <div className="text-center py-8">
        「{query}」に一致する結果は見つかりませんでした
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {results.map((result) => (
        <article
          key={result.objectID}
          className="border rounded-lg p-4 hover:shadow-md transition-shadow py-3"
        >
          <Link href={result.uri} className="block">
            <h2 className="text-xl text-primary-light font-semibold mb-2">
              {result.title}
            </h2>
            <p
              className="text-foreground whitespace-pre-line break-words overflow-hidden"
              style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
              dangerouslySetInnerHTML={{ __html: result.excerpt }}
            />
          </Link>
        </article>
      ))}
    </div>
  );
}
